import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  useAgentMemory,
  useVetoMemoryRule,
  useWipeMemory,
} from '@/hooks/profile/useAgentMemory';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { QueryErrorState } from '@/lib/components';
import { ApiError } from '@/lib/errors';
import { logger } from '@/lib/logger';

import { MemoryRuleRow } from './MemoryRuleRow';

export function AgentMemoryCard() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const { data, isLoading, error, refetch } = useAgentMemory(orgId);
  const veto = useVetoMemoryRule(orgId);
  const wipe = useWipeMemory(orgId);

  const [vetoingRuleId, setVetoingRuleId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const notFound = error instanceof ApiError && error.isNotFound();

  const handleVeto = useCallback(
    async (ruleId: string) => {
      setVetoingRuleId(ruleId);
      try {
        await veto.mutateAsync(ruleId);
      } catch (err) {
        logger.error(
          'Failed to disable agent memory rule',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'settings', orgId }
        );
        toast.error("We couldn't turn off that memory. Try again.");
      } finally {
        setVetoingRuleId(null);
      }
    },
    [veto, orgId]
  );

  const handleWipeConfirm = useCallback(async () => {
    try {
      await wipe.mutateAsync();
      toast.success('Cleared your agent memory');
    } catch (err) {
      logger.error(
        'Failed to wipe agent memory',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'settings', orgId }
      );
      toast.error("We couldn't clear your agent memory. Try again.");
      throw err;
    }
  }, [wipe, orgId]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleOpenConfirm = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  if (!orgId) {
    return null;
  }

  if (notFound && !data) {
    return null;
  }

  let body: ReactNode;
  if (isLoading && !data) {
    body = <p className='text-sm text-fg-tertiary'>Loading…</p>;
  } else if (error && !data && !notFound) {
    body = (
      <QueryErrorState
        error={error}
        label="Couldn't load your agent's memory"
        onRetry={handleRetry}
        size='sm'
      />
    );
  } else if (!data || data.length === 0) {
    body = (
      <EmptyState
        variant='noData'
        size='sm'
        title='Nothing learned yet'
        description="As you turn down or edit your agent's suggestions, what it learns will show up here."
      />
    );
  } else {
    body = (
      <>
        {data.map(rule => (
          <MemoryRuleRow
            key={rule.id}
            rule={rule}
            onVeto={handleVeto}
            isVetoing={vetoingRuleId === rule.id}
          />
        ))}
        <div className='mt-3 flex justify-end'>
          <Button color='secondary' size='sm' onClick={handleOpenConfirm}>
            Clear all
          </Button>
        </div>
      </>
    );
  }

  return (
    <div>
      <h3 className='text-base font-semibold text-fg-primary'>Agent memory</h3>
      <p className='text-sm text-fg-tertiary mt-0.5 mb-2'>
        Rules your agent has learned from your decisions and applies to future
        suggestions.
      </p>
      {body}
      <ConfirmationDialog
        isOpen={confirmOpen}
        title='Clear agent memory?'
        description="Your agent will forget everything it has learned from your decisions. This can't be undone."
        confirmLabel='Clear all'
        variant='destructive'
        isLoading={wipe.isPending}
        onConfirm={handleWipeConfirm}
        onCancel={handleCancelConfirm}
        onClose={handleCancelConfirm}
      />
    </div>
  );
}
